import { For, Show, createSignal, onMount, onCleanup, createMemo, createEffect, on, getOwner, runWithOwner } from "solid-js";
import { persistantStore } from "../../../../store/persistantStore";
import { sourcesStore } from "../../../../store/sourcesStore";
import { setSelectedSources as filterStoreSetSelectedSources } from "../../../../store/filterStore";
import { user } from "../../../../store/userStore";
/**
 * `liveSummaryFollow` (raw), not a liveMode-gated Active helper. This page never sets liveMode —
 * that is written by the live MAP container — so an Active helper would read false over a Follow
 * toggle that still says LIVE, and the refresh interval would never be created. The control and
 * the behaviour must read the same signal. This is THIS page's own switch: pausing here must not
 * pause Live Maneuvers or Live Performance.
 */
import { liveSummaryFollow, setLiveSummaryFollow } from "../../../../store/playbackStore";
import { clearLiveReportEdge, setLiveReportEdge } from "../../../../store/liveReportEdgeStore";
import { setupMediaContainerScaling } from "../../../../utils/global";
import { logPageLoad } from "../../../../utils/logging";
import { debug as logDebug } from "../../../../utils/console";
import { getLiveQueryDateYmd } from "../../../../utils/liveSessionDate";
import { ensureLiveDatasetsForProject } from "../../../../utils/liveDatasets";
import { buildColorGrouping } from "../../../../utils/colorGrouping";
import { SPEED_UNIT_KPH } from "../../../../utils/speedUnits";
import {
  LIVE_REPORT_WINDOW_ALL,
  isLiveReportWindowAll,
  liveReportWindowLabel,
  liveReportWindowStartMs,
  normalizeLiveReportWindowMinutes,
} from "../../../../utils/liveReportTimeWindow";
import FleetSourcesGate from "../../../../components/utilities/FleetSourcesGate";
import LiveFollowToggle from "../../../../components/utilities/LiveFollowToggle";
import SummaryTablesSettings from "../../../../components/menus/SummaryTablesSettings";
import ScatterTimeseries from "../../../../components/charts/ScatterTimeseries";
import { ensureFleetSourcesContextReady } from "../../../../utils/fleetSourcesReady";
import { initializeSourceSelections } from "../../../../utils/sourceInitialization";
import { persistentSettingsService } from "../../../../services/persistentSettingsService";
import { getProjectManeuverFilters } from "../../../../services/projectFiltersService";
import FleetDebriefStripTable, {
  formatDebriefValue,
  type DebriefTooltipPayload,
} from "../../helpers/FleetDebriefStripTable";
import {
  DEBRIEF_MANEUVER_METRICS,
  DEBRIEF_STRAIGHT_METRICS,
} from "../../helpers/fleetDebriefMetrics";
import { useFleetDebriefData } from "../../helpers/useFleetDebriefData";
import {
  buildSummaryTablesHistoryFilters,
  fetchDailyTrainingCoverageTimeline,
  formatBrushDateRange,
  mapCoveragePointsToTimelinePoints,
  type SummaryTablesHistoryFilters,
} from "../../helpers/dailyTrainingSettingsApi";
import "../../../../styles/FleetDebrief.css";

const { selectedClassName, selectedProjectId, selectedDate } = persistantStore;
const PAGE_TITLE = "FLEET SUMMARY";

/**
 * Follow window: each tick re-queries the trailing `followWindowMinutes` of the session, or the
 * whole session when set to ALL.
 *
 * An earlier note here assumed a 10-minute window "sat empty" because BIN 10 events and graded
 * maneuvers are only written when a phase CLOSES. Measured against a live session that is wrong:
 * the newest BIN 10 event was 1.2 minutes behind NOW(), and calling the service directly with a
 * 10-minute window returned 8 boats. The emptiness was the follow gate below, not the window.
 */
const DEBRIEF_FOLLOW_WINDOW_MINUTES = 10;

const DEBRIEF_FOLLOW_POLL_MS = 15_000;

export default function LiveFleetSummaryPage() {
  let mediaContainerRef: HTMLDivElement | undefined;
  const [gradeOptions, setGradeOptions] = createSignal<number[]>([-1, 1, 2]);
  const [stateOptions, setStateOptions] = createSignal<string[]>(["H0", "H1", "H2"]);
  const [selectedGrades, setSelectedGrades] = createSignal<number[]>([1]);
  const [selectedStates, setSelectedStates] = createSignal<string[]>(["H0"]);
  const [trainingRacing, setTrainingRacing] = createSignal<"TRAINING" | "RACING" | null>(null);

  const [filterYear, setFilterYear] = createSignal("");
  const [filterEvent, setFilterEvent] = createSignal("");
  const [filterConfigValue, setFilterConfigValue] = createSignal("");
  const [filterState, setFilterState] = createSignal("");

  const [selectedSourceIds, setSelectedSourceIds] = createSignal<Set<number>>(new Set());
  const [showTimeline, setShowTimeline] = createSignal(true);

  // Debrief display state.
  const [highlightBoat, setHighlightBoat] = createSignal("");
  const [showWhiskers, setShowWhiskers] = createSignal(false);
  /** How far back follow mode reaches, in minutes; LIVE_REPORT_WINDOW_ALL = whole session. */
  const [followWindowMinutes, setFollowWindowMinutes] = createSignal<number>(
    DEBRIEF_FOLLOW_WINDOW_MINUTES
  );
  const [tooltip, setTooltip] = createSignal<(DebriefTooltipPayload & { x: number; y: number }) | null>(null);

  // Manual timeline brush. Non-null means the user pinned a range (and follow was paused).
  const [brushedTimeRange, setBrushedTimeRange] = createSignal<{ start: Date; end: Date } | null>(null);
  // Bumped by the follow tick and by Refresh; recomputes the trailing window and re-queries.
  const [tickNonce, setTickNonce] = createSignal(0);

  const [timelinePoints, setTimelinePoints] = createSignal<Record<string, unknown>[]>([]);
  const [liveDate, setLiveDate] = createSignal(getLiveQueryDateYmd(selectedDate));

  const data = useFleetDebriefData();

  const historyFilters = createMemo((): SummaryTablesHistoryFilters =>
    buildSummaryTablesHistoryFilters({
      selectedGrades: selectedGrades(),
      selectedStates: selectedStates(),
      filterYear: filterYear(),
      filterEvent: filterEvent(),
      filterConfig: filterConfigValue(),
      trainingRacing: trainingRacing(),
    })
  );

  const resolvedSourceNames = createMemo((): string[] => {
    const sources = sourcesStore.sources();
    const ids = selectedSourceIds();
    if (ids.size === 0) {
      return sources.map((s) => String(s.source_name)).filter((n) => n.trim() !== "");
    }
    return sources
      .filter((s) => ids.has(Number(s.source_id)))
      .map((s) => String(s.source_name))
      .filter((n) => n.trim() !== "");
  });

  const dataSourcesOptions = createMemo(() =>
    sourcesStore.sources().map((source) => ({
      key: `source-${source.source_id}`,
      label: source.source_name,
      signal: [
        () => selectedSourceIds().has(Number(source.source_id)),
        (value: boolean) => {
          const current = selectedSourceIds();
          const next = new Set(current);
          if (value) { next.add(Number(source.source_id)); }
          else { next.delete(Number(source.source_id)); }
          setSelectedSourceIds(next);
        },
      ] as [() => boolean, (value: boolean) => void],
    }))
  );

  const toggleGradeFilter = (grade: number) => {
    const current = selectedGrades();
    const next = current.includes(grade) ? [] : [grade];
    setSelectedGrades(next);
  };

  const toggleStateFilter = (state: string) => {
    const current = selectedStates();
    const next = current.includes(state) ? current.filter((s) => s !== state) : [...current, state];
    setSelectedStates(next);
  };

  const mapSourceNamesToIds = (names: string[]): Set<number> => {
    const normalized = new Set(names.map((n) => String(n).toLowerCase().trim()).filter(Boolean));
    const ids = new Set<number>();
    for (const source of sourcesStore.sources()) {
      const name = String(source.source_name ?? "").toLowerCase().trim();
      const sourceId = Number(source.source_id);
      if (name && normalized.has(name) && Number.isFinite(sourceId)) {
        ids.add(sourceId);
      }
    }
    return ids;
  };

  const initializeSourcesFromPersistentSettings = async (): Promise<void> => {
    if (selectedSourceIds().size > 0) {
      return;
    }

    const sources = sourcesStore.sources();
    if (!sourcesStore.isReady() || sources.length === 0) {
      return;
    }

    try {
      const sourceNames = await initializeSourceSelections();
      if (sourceNames.length > 0) {
        const ids = mapSourceNamesToIds(sourceNames);
        if (ids.size > 0) {
          setSelectedSourceIds(ids);
          filterStoreSetSelectedSources(sourceNames);
          return;
        }
      }

      const allIds = new Set(
        sources.map((s) => Number(s.source_id)).filter((id) => Number.isFinite(id))
      );
      if (allIds.size > 0) {
        setSelectedSourceIds(allIds);
        filterStoreSetSelectedSources(
          sources.map((s) => String(s.source_name)).filter((n) => n.trim() !== "")
        );
      }
    } catch (err) {
      logDebug("LiveFleetSummary: initializeSourcesFromPersistentSettings failed", err);
      if (selectedSourceIds().size === 0) {
        const allIds = new Set(
          sources.map((s) => Number(s.source_id)).filter((id) => Number.isFinite(id))
        );
        setSelectedSourceIds(allIds);
      }
    }
  };

  /**
   * Project-level defaults, written by the settings modal's "Save default filters" button.
   *
   * That button POSTs a `maneuver_filters` project object (grades + states only) — a different
   * store from the per-user `summaryTablesFilters` that Apply writes. This page read only the
   * latter, so pressing "Save default filters" appeared to do nothing at all across a reload.
   * Applied first so a user's own Apply still wins: personal choice over the project default.
   */
  const loadProjectDefaultFilters = async (): Promise<void> => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!className || !projectId) { return; }
    try {
      const projectFilters = await getProjectManeuverFilters(className, Number(projectId), "1970-01-01");
      if (projectFilters == null) { return; }
      if (projectFilters.grades != null) { setSelectedGrades(projectFilters.grades); }
      if (projectFilters.states != null) { setSelectedStates(projectFilters.states); }
    } catch (err) {
      logDebug("LiveFleetSummary: loadProjectDefaultFilters failed", err);
    }
  };

  const loadFiltersFromPersistentSettings = async (): Promise<void> => {
    const currentUser = user();
    if (!currentUser?.user_id) { return; }
    try {
      const className = selectedClassName();
      const projectId = selectedProjectId();
      if (!className || !projectId) { return; }

      const settings = await persistentSettingsService.loadSettings(
        currentUser.user_id,
        className,
        projectId
      );

      if (settings?.summaryTablesFilters) {
        const f = settings.summaryTablesFilters;
        // `!= null`, not `.length > 0`: an empty list is a deliberate "no filter", and guarding
        // on length made it unrestorable — deselecting every state pill, applying, and reloading
        // silently reverted to the page's ["H0"] default.
        if (f.grades != null) { setSelectedGrades(f.grades); }
        if (f.states != null) { setSelectedStates(f.states); }
        if (f.trainingRacing === "TRAINING" || f.trainingRacing === "RACING") {
          setTrainingRacing(f.trainingRacing);
        }
        if (typeof f.showTimeline === "boolean") { setShowTimeline(f.showTimeline); }
      }

      if (typeof settings?.liveDebriefHighlightBoat === "string") {
        setHighlightBoat(settings.liveDebriefHighlightBoat);
      }
      if (typeof settings?.liveDebriefWhiskers === "boolean") {
        setShowWhiskers(settings.liveDebriefWhiskers);
      }
      if (settings?.liveDebriefTimeWindowMinutes != null) {
        // Binary control: anything that is not ALL means "the trailing window", whose length is
        // DEBRIEF_FOLLOW_WINDOW_MINUTES. Coercing here stops a value persisted under an earlier
        // default (60) pinning the page to a window the UI can no longer select.
        const stored = normalizeLiveReportWindowMinutes(settings.liveDebriefTimeWindowMinutes);
        setFollowWindowMinutes(
          isLiveReportWindowAll(stored) ? LIVE_REPORT_WINDOW_ALL : DEBRIEF_FOLLOW_WINDOW_MINUTES
        );
      }
    } catch (err) {
      logDebug("LiveFleetSummary: loadFiltersFromPersistentSettings failed", err);
    }
  };

  /**
   * Display options apply immediately (no Apply button), so they write their own flat keys rather
   * than going through persistAppliedSettings — that would also commit filter edits the user has
   * not applied yet. Flat because saveSettings shallow-merges: nesting these under
   * summaryTablesFilters would wipe its siblings.
   */
  const persistDisplayPreference = async (
    patch: {
      liveDebriefHighlightBoat?: string;
      liveDebriefWhiskers?: boolean;
      liveDebriefTimeWindowMinutes?: number;
    }
  ): Promise<void> => {
    const currentUser = user();
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!currentUser?.user_id || !className || !projectId) { return; }
    try {
      await persistentSettingsService.saveSettings(currentUser.user_id, className, Number(projectId), patch);
    } catch (err) {
      logDebug("LiveFleetSummary: persistDisplayPreference failed", err);
    }
  };

  const handleHighlightBoatChange = (name: string): void => {
    setHighlightBoat(name);
    void persistDisplayPreference({ liveDebriefHighlightBoat: name });
  };

  const handleWhiskersChange = (value: boolean): void => {
    setShowWhiskers(value);
    void persistDisplayPreference({ liveDebriefWhiskers: value });
  };

  /** Applies immediately: effectiveRange() depends on it, so the re-query effect below fires. */
  const handleFollowWindowChange = (value: number): void => {
    const normalized = normalizeLiveReportWindowMinutes(value);
    setFollowWindowMinutes(normalized);
    void persistDisplayPreference({ liveDebriefTimeWindowMinutes: normalized });
  };

  // ---- time range ----------------------------------------------------------------

  /**
   * The range the four queries run over.
   *
   * Following        -> the trailing followWindowMinutes, recomputed each tick; ALL = whole day.
   * Paused + brushed -> exactly what the user selected.
   * Paused, no brush -> omitted, i.e. the whole live day.
   *
   * The server applies the bound only when BOTH ends are present, so "whole day" must send neither.
   */
  const effectiveRange = createMemo((): { start: string | null; end: string | null } => {
    if (liveSummaryFollow()) {
      tickNonce(); // re-derive the trailing window on every follow tick
      const startMs = liveReportWindowStartMs(followWindowMinutes());
      if (startMs == null) { return { start: null, end: null }; }
      return { start: new Date(startMs).toISOString(), end: new Date().toISOString() };
    }
    const range = brushedTimeRange();
    if (range == null) { return { start: null, end: null }; }
    return { start: range.start.toISOString(), end: range.end.toISOString() };
  });

  /**
   * How much data the tables are actually averaging, in minutes — the question the numbers below
   * depend on. The follow toggle already reads LIVE/Paused, so this says only the span.
   */
  const rangeLabel = createMemo((): string => {
    if (liveSummaryFollow()) {
      const minutes = followWindowMinutes();
      return isLiveReportWindowAll(minutes) ? "whole session" : `last ${minutes} min`;
    }
    const range = brushedTimeRange();
    if (range == null) { return "whole session"; }
    const minutes = Math.max(1, Math.round((range.end.getTime() - range.start.getTime()) / 60_000));
    return `${minutes} min selected`;
  });

  /** Full range as a tooltip, so the exact clock times stay reachable from the short label. */
  const rangeTitle = createMemo((): string => {
    const range = brushedTimeRange();
    if (range == null) {
      return liveSummaryFollow()
        ? "Following the live edge — change the window under the gear"
        : "No time filter";
    }
    return formatBrushDateRange(range.start, range.end);
  });

  // ---- queries -------------------------------------------------------------------

  const queryContext = createMemo(() => {
    const className = selectedClassName();
    const projectId = Number(selectedProjectId());
    const date = liveDate();
    if (!className || !(projectId > 0) || !date) { return null; }
    return {
      className,
      projectId,
      dateYmd: date,
      liveOnly: true,
      sourceNames: resolvedSourceNames(),
      filters: historyFilters(),
      twsBin: null,
    };
  });

  let fetchInFlight = false;
  let rerunPending = false;

  /**
   * Single-flight with a trailing re-run. A call that lands while the four queries are out marks
   * the scope dirty and the loop re-reads it once they return. Dropping it instead (the previous
   * behaviour) lost real changes: on mount the persisted grade/state filters arrive while the
   * source-initialisation query is still in flight, and the tables then showed the DEFAULT filters
   * until the next follow tick — or, paused, indefinitely.
   */
  const runQueries = async (): Promise<void> => {
    if (fetchInFlight) { rerunPending = true; return; }
    fetchInFlight = true;
    try {
      do {
        rerunPending = false;
        const ctx = queryContext();
        if (!ctx) { break; }
        const range = effectiveRange();
        await data.load({ ...ctx, brushStartTime: range.start, brushEndTime: range.end });
      } while (rerunPending);
    } finally {
      fetchInFlight = false;
    }
  };

  /**
   * Coverage markers for the timeline strip. Only re-fetched while following: ScatterTimeseries
   * clears its brush whenever it rebuilds, so refreshing the points under a paused, manually
   * brushed selection would silently wipe that selection.
   */
  const loadCoverageTimeline = async (): Promise<void> => {
    const ctx = queryContext();
    if (!ctx) { return; }
    try {
      const result = await fetchDailyTrainingCoverageTimeline({
        className: ctx.className,
        projectId: ctx.projectId,
        legType: "upwind",
        speedUnit: SPEED_UNIT_KPH,
        twsBin: null,
        sourceNames: ctx.sourceNames,
        startDate: ctx.dateYmd,
        endDate: ctx.dateYmd,
        filters: ctx.filters,
        liveOnly: true,
      });
      if (!result.ok) {
        logDebug("LiveFleetSummary: coverage timeline failed", result.message);
        return;
      }
      // The user may have brushed while this was in flight. Applying the points now would redraw
      // the strip and destroy the selection they just made, so drop the result instead.
      if (brushedTimeRange() != null) { return; }

      const points = mapCoveragePointsToTimelinePoints(result.data.coverage_points, SPEED_UNIT_KPH);
      setTimelinePoints(points);

      // Data-age badge: the newest marker on the plot.
      let newest = 0;
      for (const p of result.data.coverage_points) {
        const ms = new Date(p.start_time).getTime();
        if (Number.isFinite(ms) && ms > newest) { newest = ms; }
      }
      if (newest > 0) {
        setLiveReportEdge({ atMs: newest, what: "fleet-debrief", label: "last points" });
      }
    } catch (err) {
      logDebug("LiveFleetSummary: coverage timeline threw", err);
    }
  };

  /**
   * Legend groups in the shape ScatterTimeseries declares. buildColorGrouping returns
   * `{ key, items }`, so map it rather than passing it through — with color="SOURCE" and a
   * colorScale the component never reads groups, which is why the mismatch goes unnoticed
   * elsewhere, but the fallback colour path does use `.name`/`.color`.
   */
  const timelineGroups = createMemo((): Array<{ name: string; color: string }> => {
    const pts = timelinePoints();
    if (pts.length === 0) { return []; }
    const { groups, getItemColor } = buildColorGrouping(pts, "SOURCE");
    return groups.map((g) => ({
      name: String(g.key),
      color: g.items.length > 0 ? getItemColor(g.items[0]) : "#6b7280",
    }));
  });

  const timelineColorScale = createMemo((): ((v: string) => string) | undefined => {
    const pts = timelinePoints();
    if (pts.length === 0) { return undefined; }
    const { getItemColor } = buildColorGrouping(pts, "SOURCE");
    return (normalizedLower: string) =>
      getItemColor({ source_name: normalizedLower } as Record<string, unknown>);
  });

  /**
   * Stable key. Deliberately excludes the point count: ScatterTimeseries rebuilds when `key`
   * changes and a rebuild clears the brush, so keying on a live-growing count would wipe a manual
   * selection on the next tick that adds a marker.
   */
  const timelineKey = createMemo(
    () =>
      `fleet-debrief-tl-${selectedClassName()}-${selectedProjectId()}-${liveDate()}-${resolvedSourceNames().join(",")}`
  );

  const handleBrush = (detail: { eventIds: number[]; startTime: Date | null; endTime: Date | null }): void => {
    const hasRange = detail.startTime != null && detail.endTime != null;
    if (!hasRange) {
      // An empty drag clears the pin but must not resume follow — follow is the user's to restart.
      setBrushedTimeRange(null);
      return;
    }
    // A brush is a deliberate "show me this instead"; keep the live window from overwriting it.
    // Safe to call directly (unlike LiveManeuvers' guarded effect) because this callback only ever
    // fires from a real pointer gesture in this window, not from a rehydrated cross-window signal.
    setLiveSummaryFollow(false);
    setBrushedTimeRange({ start: detail.startTime!, end: detail.endTime! });
  };

  onMount(() => {
    logDebug("LiveFleetSummary: mount");
    logPageLoad("live/reports/LiveFleetSummary.tsx", PAGE_TITLE, "Loaded");
    const owner = getOwner();

    void (async () => {
      const className = selectedClassName();
      const projectId = selectedProjectId();
      await ensureFleetSourcesContextReady({ className, projectId });
      await initializeSourcesFromPersistentSettings();
      await loadProjectDefaultFilters();
      await loadFiltersFromPersistentSettings();

      if (className && projectId) {
        await ensureLiveDatasetsForProject(Number(projectId), className);
      }
      setLiveDate(getLiveQueryDateYmd(selectedDate));

      await loadCoverageTimeline();
      await runQueries();

      runWithOwner(owner, () => {
        const cleanupScaling = setupMediaContainerScaling({
          logPrefix: "LiveFleetSummary",
          scaleToWidth: true,
          getMediaContainer: () => mediaContainerRef ?? null,
        });
        onCleanup(() => cleanupScaling());
      });
    })();
  });

  onCleanup(() => clearLiveReportEdge("fleet-debrief"));

  createEffect(() => {
    const ids = selectedSourceIds();
    const sources = sourcesStore.sources();
    if (ids.size === 0 || sources.length === 0) {
      return;
    }
    const names: string[] = [];
    sources.forEach((source) => {
      const sourceId = Number(source.source_id);
      if (ids.has(sourceId) && source.source_name) {
        names.push(String(source.source_name));
      }
    });
    if (names.length > 0) {
      filterStoreSetSelectedSources(names);
    }
  });

  // Follow tick. onCleanup is registered INSIDE the effect body: a disposer returned from
  // createEffect is discarded, which would leak one interval per follow toggle.
  createEffect(
    on(liveSummaryFollow, (following) => {
      if (!following) { return; }
      // Tick once straight away rather than waiting a full interval: pressing play should land the
      // trailing window immediately, exactly as brushing the timeline does. Bumping tickNonce
      // re-derives effectiveRange, which is the same signal the brush changes, so both go through
      // one re-query path.
      setTickNonce((n) => n + 1);
      void loadCoverageTimeline();
      const timer = setInterval(() => {
        setTickNonce((n) => n + 1);
        void loadCoverageTimeline();
      }, DEBRIEF_FOLLOW_POLL_MS);
      onCleanup(() => clearInterval(timer));
    })
  );

  // Re-query whenever the effective scope changes. defer: the initial load runs in onMount.
  createEffect(
    on(
      () => [effectiveRange(), historyFilters(), resolvedSourceNames(), liveDate()] as const,
      () => { void runQueries(); },
      { defer: true }
    )
  );

  const boatNames = createMemo((): string[] => {
    const names = new Set<string>();
    for (const rows of [data.uwRows(), data.dwRows(), data.tackRows(), data.gybeRows()]) {
      for (const row of rows) {
        const name = String(row.source_name ?? "").trim();
        if (name) { names.add(name); }
      }
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  });

  createEffect(() => {
    const names = boatNames();
    if (names.length > 0 && !names.some((n) => n.toLowerCase() === highlightBoat().toLowerCase())) {
      setHighlightBoat(names[0]);
    }
  });

  const getColor = (name: string): string => sourcesStore.getSourceColor(name) ?? "#6b7280";

  const twsLabelFor = (rows: () => Record<string, unknown>[]) => (): string => {
    const vals = rows()
      .map((r) => Number(r.tws_avg))
      .filter((v) => Number.isFinite(v));
    if (vals.length === 0) { return ""; }
    const mean = vals.reduce((a, v) => a + v, 0) / vals.length;
    return `TWS avg ${mean.toFixed(1)} kph`;
  };

  const onTipShow = (ev: MouseEvent, tip: DebriefTooltipPayload) => {
    setTooltip({ ...tip, x: ev.clientX, y: ev.clientY });
  };
  const onTipMove = (ev: MouseEvent) => {
    const current = tooltip();
    if (current) { setTooltip({ ...current, x: ev.clientX, y: ev.clientY }); }
  };
  const onTipHide = () => setTooltip(null);

  const persistAppliedSettings = async (): Promise<void> => {
    const currentUser = user();
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!currentUser?.user_id || !className || !projectId) { return; }
    try {
      await persistentSettingsService.saveSettings(currentUser.user_id, className, Number(projectId), {
        summaryTablesFilters: {
          grades: selectedGrades(),
          states: selectedStates(),
          trainingRacing: trainingRacing(),
          showTimeline: showTimeline(),
        },
      });
    } catch (err) {
      logDebug("LiveFleetSummary: persistAppliedSettings failed", err);
    }
  };

  const columns = [
    {
      title: "UPWIND",
      straightRows: data.uwRows,
      straightEmpty: "No upwind straight-line data for this range.",
      maneuverTitle: "Tack",
      maneuverRows: data.tackRows,
      maneuverEmpty: "No tacks for this range.",
    },
    {
      title: "DOWNWIND",
      straightRows: data.dwRows,
      straightEmpty: "No downwind straight-line data for this range.",
      maneuverTitle: "Gybe",
      maneuverRows: data.gybeRows,
      maneuverEmpty: "No gybes for this range.",
    },
  ];

  return (
    <div ref={mediaContainerRef} id="media-container" class="cheat-sheet-page training-summary-page">
      <FleetSourcesGate message="Loading fleet sources...">
        <div class="container relative">
          <div class="performance-charts-scroll-container">
            <div class="performance-charts-scroll-inner cheat-sheet-scroll-inner">
              <header class="training-summary-header">
                <h1 class="report-page-title cheat-sheet-title">{PAGE_TITLE}</h1>
              </header>

              {/* Only the play/pause and the gear live out here; everything else is in the gear. */}
              <div class="fd-toolbar">
                <LiveFollowToggle scope="report" page="summary" />
                <span class="fd-range-label" title={rangeTitle()}>{rangeLabel()}</span>
                <SummaryTablesSettings
                  useIconTrigger={true}
                  dataSourcesOptions={dataSourcesOptions()}
                  gradeOptions={gradeOptions()}
                  stateOptions={stateOptions()}
                  selectedGrades={() => selectedGrades()}
                  selectedStates={() => selectedStates()}
                  selectedTrainingRacing={trainingRacing()}
                  onTrainingRacingFilterChange={setTrainingRacing}
                  setGradeOptions={setGradeOptions}
                  setStateOptions={setStateOptions}
                  toggleGradeFilter={toggleGradeFilter}
                  toggleStateFilter={toggleStateFilter}
                  filterYear={filterYear}
                  filterEvent={filterEvent}
                  filterConfig={filterConfigValue}
                  filterState={filterState}
                  onFilterYearChange={setFilterYear}
                  onFilterEventChange={setFilterEvent}
                  onFilterConfigChange={setFilterConfigValue}
                  onFilterStateChange={setFilterState}
                  onApplyFilters={() => void persistAppliedSettings()}
                  showTimeline={showTimeline}
                  onTimelineChange={setShowTimeline}
                  hideProjectScopeFilters={true}
                  extraDisplayOptions={
                    <>
                      <div>
                        <div class="fd-opt-title">Highlight</div>
                        <select
                          class="fd-opt-select"
                          value={highlightBoat()}
                          onChange={(e) => handleHighlightBoatChange(e.currentTarget.value)}
                        >
                          <For each={boatNames()}>
                            {(name) => <option value={name}>{name}</option>}
                          </For>
                        </select>
                      </div>

                      <div>
                        <div class="fd-opt-title">Fleet spread</div>
                        <div class="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleWhiskersChange(!showWhiskers())}
                            class={`px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${showWhiskers() ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700"
                              }`}
                            onContextMenu={(e) => e.preventDefault()}
                          >
                            {showWhiskers() ? 'ON' : 'OFF'}
                          </button>
                          <span class="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                            ±1σ whiskers
                          </span>
                        </div>
                      </div>

                      {/* Same LAST HOUR <-> ALL pill as Live Performance / Live Maneuvers; applies at once. */}
                      <div>
                        <div class="fd-opt-title">Follow window</div>
                        <div class="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handleFollowWindowChange(
                                isLiveReportWindowAll(followWindowMinutes())
                                  ? DEBRIEF_FOLLOW_WINDOW_MINUTES
                                  : LIVE_REPORT_WINDOW_ALL
                              )
                            }
                            class={`px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${!isLiveReportWindowAll(followWindowMinutes()) ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700"
                              }`}
                            onContextMenu={(e) => e.preventDefault()}
                          >
                            {liveReportWindowLabel(followWindowMinutes())}
                          </button>
                          <span class="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                            {isLiveReportWindowAll(followWindowMinutes())
                              ? "Whole session while following — heavier on every tick"
                              : `Most recent ${DEBRIEF_FOLLOW_WINDOW_MINUTES} minutes while following`}
                          </span>
                        </div>
                      </div>
                    </>
                  }
                />
              </div>

              <div class="fleet-debrief-page fd-embedded">
                <Show when={showTimeline() && timelinePoints().length > 0}>
                  <ScatterTimeseries
                    key={timelineKey()}
                    aggregates={timelinePoints()}
                    color="SOURCE"
                    groups={timelineGroups()}
                    colorScale={timelineColorScale()}
                    skipSelectedSourcesFilter={true}
                    isHistoryPage={false}
                    onBrushSelect={handleBrush}
                  />
                </Show>

                <Show when={data.error()}>
                  <p class="fd-error">{data.error()}</p>
                </Show>
                <Show when={data.loading() && queryContext() != null}>
                  <p class="fd-loading">Loading fleet debrief…</p>
                </Show>

                <div class="fd-columns">
                  <For each={columns}>
                    {(col) => (
                      <div class="fd-col">
                        <h2 class="fd-col-title">{col.title}</h2>
                        <FleetDebriefStripTable
                          title="Straight line"
                          twsLabel={twsLabelFor(col.straightRows)}
                          metrics={DEBRIEF_STRAIGHT_METRICS}
                          rows={col.straightRows}
                          highlightBoat={highlightBoat}
                          showWhiskers={showWhiskers}
                          getColor={getColor}
                          emptyMessage={col.straightEmpty}
                          onTipShow={onTipShow}
                          onTipMove={onTipMove}
                          onTipHide={onTipHide}
                        />
                        <FleetDebriefStripTable
                          title={col.maneuverTitle}
                          metrics={DEBRIEF_MANEUVER_METRICS}
                          rows={col.maneuverRows}
                          highlightBoat={highlightBoat}
                          showWhiskers={showWhiskers}
                          getColor={getColor}
                          emptyMessage={col.maneuverEmpty}
                          onTipShow={onTipShow}
                          onTipMove={onTipMove}
                          onTipHide={onTipHide}
                        />
                      </div>
                    )}
                  </For>
                </div>

                <Show when={tooltip()}>
                  {(tip) => (
                    <div
                      class="fd-tooltip"
                      style={{
                        left: `${Math.min(tip().x + 14, window.innerWidth - 230)}px`,
                        top: `${Math.min(tip().y + 14, window.innerHeight - 130)}px`,
                      }}
                    >
                      <div class="fd-tooltip-boat">
                        <span class="fd-chip-swatch" style={{ background: tip().color }} />
                        {tip().boat}
                        <span class="fd-tooltip-context"> · {tip().section} · {tip().metric.label}</span>
                      </div>
                      <table>
                        <tbody>
                          <tr><td>Mean</td><td>{formatDebriefValue(tip().stat.mean, tip().metric.dp)} {tip().metric.unit}</td></tr>
                          <tr><td>σ</td><td>{tip().stat.sd == null ? "—" : tip().stat.sd!.toFixed(2)}</td></tr>
                          <tr><td>Count</td><td>{tip().stat.count == null ? "—" : tip().stat.count}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </Show>
              </div>
            </div>
          </div>
        </div>
      </FleetSourcesGate>
    </div>
  );
}
